import {
  IonButton,
  IonContent,
  IonLoading,
  IonModal,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import React, { useCallback, useEffect, useState } from 'react';

import {
  ButtonContainer,
  Logo,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { Icon, Text } from '@/components/memo';
import { useDentist } from '@/contexts';
import { useToast } from '@/hooks';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { arrowBackOutline, checkmarkCircleOutline } from 'ionicons/icons';

import './UpdatePatientVerification.css';

interface UpdatePatientVerificationProps {
  isOpen: boolean;
  onClose: () => void;
  presentingElement?: HTMLElement | undefined;
}

const UpdatePatientVerification: React.FC<UpdatePatientVerificationProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const { selectedDentist: dentist, updateDentistMutation } = useDentist();
  const displayToast = useToast();
  const [patientVerificationEnabled, setPatientVerificationEnabled] =
    useState<boolean>(false);

  useEffect(() => {
    if (dentist) {
      setPatientVerificationEnabled(
        dentist.patient_verification_enabled || false
      );
    }
  }, [dentist?.patient_verification_enabled]);

  const copy = {
    header: (
      <>
        Update <Text color="primary">patient</Text> verification settings
      </>
    ),
    message: `Please select whether patients of ${formatDentistName({
      dentist
    })} should verify their identity via OTP.`,
    buttonText: 'Update',
    selectLabel: 'Require Verification via OTP',
    selectPlaceholder: 'Select verification method'
  };

  const onNext = useCallback(async () => {
    if (dentist?.id) {
      await updateDentistMutation.mutateAsync({
        dentistId: dentist.id,
        dentist: {
          ...dentist,
          patient_verification_enabled: patientVerificationEnabled
        }
      });
      displayToast({
        message: 'Patient verification settings updated successfully.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
      onClose();
    }
  }, [patientVerificationEnabled, dentist, updateDentistMutation, onClose]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={presentingElement}
    >
      <IonContent>
        <Padding withSafeArea={!presentingElement}>
          <div className="update-patient-verification">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={onClose}
            />
            <Spacer top={16} bottom={24}>
              <h1 className="update-patient-verification__header">
                {copy.header}
              </h1>
              <p className="update-patient-verification__message">
                {copy.message}
              </p>
            </Spacer>

            <Spacer bottom={124}>
              <div className="update-patient-verification__form">
                <div className="update-patient-verification__input">
                  <IonSelect
                    label={copy.selectLabel}
                    labelPlacement="stacked"
                    placeholder={copy.selectPlaceholder}
                    value={patientVerificationEnabled}
                    onIonChange={(e: CustomEvent) =>
                      setPatientVerificationEnabled(e.detail.value)
                    }
                  >
                    <IonSelectOption value={true}>Yes</IonSelectOption>
                    <IonSelectOption value={false}>No</IonSelectOption>
                  </IonSelect>
                </div>
              </div>
            </Spacer>

            <ButtonContainer>
              <IonButton
                id="next-button"
                onClick={onNext}
                expand="block"
                fill="solid"
                size="default"
                color="primary"
                strong={true}
                disabled={updateDentistMutation.isPending}
              >
                <Icon slot="start" icon={checkmarkCircleOutline} />
                {copy.buttonText}
              </IonButton>
            </ButtonContainer>
          </div>
        </Padding>
        <IonLoading
          isOpen={updateDentistMutation.isPending}
          message="Loading..."
          spinner="crescent"
        />
      </IonContent>
    </IonModal>
  );
};

export default UpdatePatientVerification;
